import { api } from 'api'
import { AxiosResponse } from 'axios'
import { format } from 'date-fns'
import { Assignment } from 'typings/common_defs'
import { indexBy, path } from 'ramda'
import {
  CalendarViewListing,
  CalendarViewShift,
  CalendarViewWork,
  CalendarViewWorker,
} from 'pages/AssignmentDetailPage/types'
import { getShiftDate } from 'pages/AssignmentDetailPage/utils/getShiftDate'

export type WorkerStatusFilter = 'assigned' | 'unassigned' | 'all'

type GetCalendarEventsRequest = {
  assignmentId: number
  startDate: Date
  endDate: Date
  workStatus: WorkerStatusFilter
}

type GetCalendarEventsResponse = {
  shiftsById: Record<CalendarViewShift['id'], CalendarViewShift>
  shiftsByDay: Record<
    string /**** yyyy-MM-dd */,
    Pick<CalendarViewShift, 'id'>[]
  >
  workers: CalendarViewWorker[]
  listings: CalendarViewListing[]
}

type GetCalendarEventsResponseDTO = {
  data: {
    company: {
      assignment: Assignment & {
        shifts: (CalendarViewShift & {
          work: (CalendarViewWork & {
            worker: Pick<CalendarViewWorker, 'id' | 'name'>
          })[]
        })[]

        workers: CalendarViewWorker[]

        listings: {
          id: number
          numWorkersPending: number
          shifts: {
            id: number
            numOpenSpots: number
          }[]
        }[]
      }
    }
  }
}

export const getCalendarEvents = async ({
  assignmentId,
  startDate,
  endDate,
  workStatus = 'assigned',
}: GetCalendarEventsRequest): Promise<GetCalendarEventsResponse> => {
  const startDateDay = format(startDate, 'yyyyMMdd')
  const endDateDay = format(endDate, 'yyyyMMdd')

  const data = await api.post<
    GetCalendarEventsResponseDTO,
    AxiosResponse<GetCalendarEventsResponseDTO>
  >('/company/graphql_dispatch/assignment_calendar_view_41b5c554', {
    assignmentId,
    startDateDay,
    endDateDay,
    workerStatus: workStatus,
    workerStartDate: workStatus === 'assigned' ? startDateDay : undefined,
  })

  const result = path(['data', 'data', 'company', 'assignment'], data) || []
  const shiftsByDay = {}
  const shiftsById = {}

  result.shifts.forEach(({ work, ...shift }) => {
    const date = getShiftDate(shift)
    shiftsByDay[date] = (shiftsByDay[date] || []).concat({ id: shift.id })
    shiftsById[shift.id] = {
      ...shift,
      workByWorker: indexBy((w) => String(w?.worker?.id), work),
    }
  })

  return {
    shiftsById,
    shiftsByDay,
    workers: result.workers,
    listings: result.listings,
  }
}
